import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { AbstractControl, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { SelectListModel } from '@app/core/models/select.list.model';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { LoadBidApproveRejectRequestModel, LoadBidsOffersModel } from '../load-bids-offers.model';
import { LoginHelper } from '@app/core/helpers/login.helper';
import { LoadService } from '@app/modules/load/load.service';
import { FormHelper, NumberLengthValidator } from '@app/core/helpers/form.helper';
import { Constants } from '@app/core/constants/constants';
import { ToasterService } from '@app/core/services/toaster.service';
import { LoadCarrierBidStage } from '@app/core/constants/enums';

@Component({
  selector: 'app-approve-reject-bid',
  templateUrl: './approve-reject-bid.component.html',
  styles: [
  ],
})
export class ApproveRejectBidComponent implements OnInit {
  readonly LoadCarrierBidStage = LoadCarrierBidStage;
  approveRejectBidForm!: FormGroup;
  @Input() approveBid!: boolean;
  approveRejectReasons: SelectListModel[] = [];
  @Input() carrierBid!: LoadBidsOffersModel;
  @Input() isApprove!: boolean;
  @Input() isExpire!: boolean;
  @Input() isOverlayPanel = false
  @Output() closeOverlayPanel = new EventEmitter<boolean>();
  @Input() isRejectClicked = false;

  constructor(
    private readonly fb: FormBuilder,
    private readonly ref: DynamicDialogRef,
    private readonly dynamicDialogService: DynamicDialogConfig,
    private readonly service: LoadService,
    private readonly toastService: ToasterService
  ) {
    this.getRelatedData();
    this.carrierBid = this.dynamicDialogService.data?.carrierBid;
    this.approveBid = this.dynamicDialogService.data?.approveBid;
    this.isApprove = this.dynamicDialogService.data?.isApprove;
    this.isExpire = this.dynamicDialogService.data?.isExpire;
  }

  ngOnInit(): void {
    this.initForm();
  }

  /**
   * Get method for amount label.
   */
  get amountLabel(): string {
    return this.isApprove ? Constants.approveBidAmmountLabel : Constants.counterBidAmmountLabel;
  }

  get additionalCommentsLabel(): string {
    return this.isExpire ? Constants.reason : Constants.additionalComments;
  }

  getRelatedData(): void {
    this.service.approveRejectBidReasons().subscribe((response) => {
      if (response.data) {
        this.approveRejectReasons = response.data;
        this.approveRejectReasons.unshift({
          text: Constants.pleaseSelectDefaultText,
          value: null
        });
      }
    })
  }

  initForm(): void {
    this.approveRejectBidForm = this.fb.group({
      approvedAmount: new FormControl(this.carrierBid?.bidAmount, {
        validators: [
          NumberLengthValidator(7)
        ]
      }),
      loadCarrierBidRejectionReasonId: new FormControl(null),
      additionalComment: new FormControl(null, {
        validators: [
          Validators.maxLength(250)
        ]
      }),
      loadCarrierBidId: new FormControl(this.carrierBid?.loadCarrierBidId),
      modifiedBy: new FormControl(LoginHelper.GetUserDetail()?.userId),
      loadDetailId: new FormControl(this.carrierBid?.loadDetailId),
      bidStage: new FormControl(this.carrierBid?.bidStage)
    });

    if (this.approveBid) {
      this.getControl('approvedAmount').valueChanges.subscribe((value) => {
        if (value !== null && !isNaN(value)) {
          if (value > this.carrierBid?.bidAmount) {
            this.getControl('approvedAmount').setErrors({ invalidbidamount: true });
          } else if (value < 0) {
            this.getControl('approvedAmount').setErrors({ min: { min: 0, actual: value } });
          } else {
            this.getControl('approvedAmount').setErrors(null);
          }
        }
      });
      this.getControl('approvedAmount').setValidators([Validators.required]);
      this.getControl('approvedAmount').updateValueAndValidity();
    } else if (!this.isExpire) {
      this.getControl('loadCarrierBidRejectionReasonId').setValidators([Validators.required]);
      this.getControl('loadCarrierBidRejectionReasonId').updateValueAndValidity();
    }

    if (this.isApprove) {
      this.getControl('approvedAmount').disable();
    }

    if (!this.approveBid && this.isExpire) {
      this.getControl('additionalComment').setValidators([Validators.required, Validators.maxLength(250)]);
      this.getControl('additionalComment').updateValueAndValidity();
    }
  }

  getControl(control: string): AbstractControl {
    return this.approveRejectBidForm.controls[control];
  }

  save(): void {
    if (this.approveRejectBidForm.invalid) {
      FormHelper.MarkFormGroupDirty(this.approveRejectBidForm);
      return;
    }
    const formValue = this.approveRejectBidForm.value;
    const bidApproveRejectRequest: LoadBidApproveRejectRequestModel = {
      ...formValue,
      approvedAmount: this.getControl('approvedAmount').getRawValue()
    }

    this.service.approveRejectCarrierBid(bidApproveRejectRequest, this.approveBid).subscribe((response) => {
      if (response.data) {
        this.toastService.showSuccessMessage(response.userMessage);
        this.service.setBidApproval(this.approveBid);
        if (!this.isOverlayPanel) {
          this.ref.close(response.data);
        }
        else {
          this.ref.close(false);
        }
      }
    });
  }

  cancel(): void {
    if (!this.isOverlayPanel) {
      this.ref.close();
    }
    else {
      this.closeOverlayPanel.emit(false);
    }
  }

  /**
 * On Counter button click
 * @returns 
 */
  onCounterClick() {
    if (this.approveRejectBidForm.invalid) {
      FormHelper.MarkFormGroupDirty(this.approveRejectBidForm);
      return;
    }
    const formValue = this.approveRejectBidForm.value;
    const bidApproveRejectRequest: LoadBidApproveRejectRequestModel = {
      ...formValue,
      approvedAmount: this.getControl('approvedAmount').getRawValue()
    }

    this.service.counterCarrierBid(bidApproveRejectRequest).subscribe((response) => {
      if (response.data) {
        this.toastService.showSuccessMessage(response.userMessage);
        this.service.setBidApproval(this.approveBid);
        this.ref.close(response.data);
      }
    });
  }

  onDeadClick() {
    if (this.approveRejectBidForm.invalid) {
      FormHelper.MarkFormGroupDirty(this.approveRejectBidForm);
      return;
    }
    const formValue = this.approveRejectBidForm.value;
    const bidApproveRejectRequest: LoadBidApproveRejectRequestModel = {
      ...formValue
    }

    this.service.expireCarrierBid(bidApproveRejectRequest).subscribe((response) => {
      if (response.data) {
        this.toastService.showSuccessMessage(response.userMessage);
        this.service.setBidApproval(this.approveBid);
        this.ref.close(response.data);
      }
    });
  }
}